import { Button, ButtonProps, CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { useAnalytics } from "src/hooks/contexts/useAnalytics";

interface LoadingButtonProps extends ButtonProps {
  isLoading?: boolean;
  loadingIndicator?: string;
  spinnerPosition?: "end" | "start";
  async?: boolean;
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  isLoading,
  disabled,
  async,
  loadingIndicator,
  spinnerPosition,
  children,
  startIcon,
  endIcon,
  onClick,
  ...rest
}): JSX.Element => {
  const [processingAsync, setProcessingAsync] = useState(false);
  const { track } = useAnalytics();

  const finalIsLoading = processingAsync || isLoading;
  const finalDisabled = disabled || finalIsLoading;
  const showLoadingIndicator = finalIsLoading && loadingIndicator;

  const processOnClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (async) {
      setProcessingAsync(true);
    }
    void track("click", {
      name: (e.target as HTMLButtonElement).textContent || "unknown",
    });
    if (onClick) {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await onClick(e);
    }

    setProcessingAsync(false);
  };

  const renderSpinner = () => {
    return (
      <CircularProgress
        sx={{ marginLeft: spinnerPosition === "end" ? 2 : undefined }}
        color="inherit"
        size={18}
      />
    );
  };

  const renderChildren = () => {
    if (showLoadingIndicator) {
      return loadingIndicator;
    }
    if (finalIsLoading && !spinnerPosition) {
      return renderSpinner();
    }
    return children;
  };

  return (
    <Button
      {...rest}
      startIcon={
        finalIsLoading && spinnerPosition === "start"
          ? renderSpinner()
          : startIcon
      }
      endIcon={
        finalIsLoading && spinnerPosition === "end" ? renderSpinner() : endIcon
      }
      disabled={finalDisabled}
      onClick={processOnClick}
    >
      {renderChildren()}
    </Button>
  );
};
